import { useEffect } from "react";
import { motion } from "framer-motion";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { WingmanChat } from "@/components/WingmanChat";
import { trackEvent } from "@/utils/analytics";

const EASE = [0.16, 1, 0.3, 1];

const ENTRIES = [
  {
    version: "V8",
    date: "September 2026",
    title: "Signature, editorial",
    items: [
      "New Sign engine — draw or type your signature, place it anywhere on a PDF, download it signed and dated",
      "DocuSign-style field placement and sender countersigning in the Legal workspace",
      "A cleaner Classic Editorial look across the whole site, now with pill-shaped buttons throughout",
      "Styled calendar date picker in the invoice builder",
    ],
  },
  {
    version: "V7",
    date: "August 2026",
    title: "Get seen",
    items: [
      "27 programmatic tool & guide pages with exact-match search titles, FAQ schema, and build-time prerendering",
      "Branded link-preview cards for every page — shared links now show the exact tool",
      "Target-size compression: compress a PDF to exactly 100KB, 200KB, 500KB, 1MB, or 2MB",
      "PDF to JPG output alongside PNG",
      "Traffic & funnel visibility in the admin command center",
    ],
  },
  {
    version: "V6",
    date: "August 2026",
    title: "The client loop",
    items: [
      "Client portal — one secure link per client for signed contracts and invoice payment",
      "Invoice builder: live preview with zoom, company logo, due dates, payment terms, and saved templates",
      "Edit PDF: place text anywhere, drag it pixel-perfect, rotate or delete pages visually",
      "Invoice payment reminders and a weekly admin digest email",
      "Admin email pipeline — every email the platform sends, visible in one feed",
    ],
  },
  {
    version: "V5",
    date: "August 2026",
    title: "The legal desk",
    items: [
      "Contracts with built-in e-signatures and a tamper-evident audit trail",
      "Secure client signing links — no account needed for signers",
      "Stripe invoices with hosted payment pages",
      "Five new engines: Protect, Unlock, Scrub, Edit, and Invoice",
      "Light and dark mode across the whole workspace",
    ],
  },
  {
    version: "V4",
    date: "August 2026",
    title: "The conversion engine",
    items: [
      "The dark, conversion-focused Doclyx identity",
      "Guest funnel — first conversion free, no account",
      "Fourteen-day trials on every plan",
      "Lenis-smooth landing with live tool demonstrations",
    ],
  },
];

export default function Changelog() {
  useEffect(() => {
    document.title = "What's New — Doclyx Changelog";
    trackEvent("page_view", { source: "changelog" });
    return () => {
      document.title = "PDF Tools for M&A & Legal Teams — Compress, Merge, Redact | Doclyx";
    };
  }, []);

  return (
    <main className="bg-page text-ink min-h-screen" data-testid="changelog-page">
      <Nav />
      <section className="pt-32 md:pt-40 pb-16 md:pb-24 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE }}>
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-mist block mb-5">Changelog</span>
            <h1 className="font-medium tracking-tight leading-tight text-4xl md:text-5xl mb-4" data-testid="changelog-title">
              What's new in Doclyx
            </h1>
            <p className="text-base text-fog leading-relaxed mb-14">
              Every release, in plain language — newest first.
            </p>
          </motion.div>

          <div className="space-y-12">
            {ENTRIES.map((e, i) => (
              <motion.div
                key={e.version}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, ease: EASE, delay: i * 0.05 }}
                className="relative pl-8 border-l border-hairline"
                data-testid={`changelog-entry-${e.version.toLowerCase()}`}
              >
                <span className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full bg-azure" />
                <div className="flex items-baseline gap-3 mb-3 flex-wrap">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-azure">{e.version}</span>
                  <span className="font-mono text-[11px] text-mist">{e.date}</span>
                </div>
                <h2 className="text-xl md:text-2xl font-medium tracking-tight text-ink mb-4">{e.title}</h2>
                <ul className="space-y-2.5">
                  {e.items.map((item) => (
                    <li key={item} className="text-sm text-fog leading-relaxed flex gap-2.5">
                      <span className="text-azure mt-0.5 shrink-0">—</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <Footer />
      <WingmanChat />
    </main>
  );
}
